import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { postAdminUserUpdate } from "../services/callApi";
import {
  getApiStatus,
  getManagementApiErrorMessage,
} from "../utils/managementApiError";
import { buildAdminUserUpdatePayload } from "../utils/adminUserUpdatePayload";

const toFormData = (adminUser) => ({
  adminUserName: adminUser?.adminUserName ?? "",
  role: adminUser?.role ?? "admin",
  companyId: adminUser?.companyId ?? "",
  isActive: adminUser?.isActive !== false,
});

function AdminUserDetail({ adminUser, companies, onBack, onUpdated, onForbidden }) {
  const [formData, setFormData] = useState(() => toFormData(adminUser));
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateInFlightRef = useRef(false);

  useEffect(() => {
    setFormData(toFormData(adminUser));
    setErrorMessage("");
    setSuccessMessage("");
  }, [adminUser]);

  const handleChange = (name) => (event) => {
    const value = name === "isActive" ? event.target.checked : event.target.value;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleRoleChange = (event) => {
    const role = event.target.value;
    setFormData((current) => ({
      ...current,
      role,
    }));
  };

  const handleApiError = (error) => {
    const status = getApiStatus(error);
    if (status === 401) {
      window.location.href = "/login";
      return;
    }
    if (status === 403) {
      onForbidden("adminUserList");
      return;
    }
    setErrorMessage(getManagementApiErrorMessage(error));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (updateInFlightRef.current) {
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");

    if (!formData.adminUserName.trim()) {
      setErrorMessage("氏名を入力してください。");
      return;
    }
    if (formData.role === "admin" && formData.companyId === "") {
      setErrorMessage("admin の場合は会社を選択してください。");
      return;
    }

    const payload = buildAdminUserUpdatePayload(adminUser, formData);

    updateInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await postAdminUserUpdate(payload);
      await onUpdated(adminUser.adminUserId);
      setSuccessMessage("管理者情報を更新しました。");
    } catch (error) {
      handleApiError(error);
    } finally {
      updateInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          管理者詳細
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            管理者一覧
          </Link>
          <Typography color="text.primary">詳細</Typography>
        </Breadcrumbs>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={3}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: { xs: "40%", sm: "25%" } }}>ID</Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Typography>{adminUser.adminUserId}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: { xs: "40%", sm: "25%" } }}>作成日時</Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Typography sx={{ whiteSpace: "nowrap" }}>{adminUser.createdAt ?? "－"}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ width: { xs: "40%", sm: "25%" } }}>
              ログインID
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Typography sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              {adminUser?.cognitoUsername ?? "－"}
            </Typography>
          </Box>

          <TextField
            fullWidth
            required
            label="氏名"
            value={formData.adminUserName}
            onChange={handleChange("adminUserName")}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} disabled={isSubmitting}>
            <InputLabel id="admin-user-role-label">ロール</InputLabel>
            <Select
              labelId="admin-user-role-label"
              label="ロール"
              value={formData.role}
              onChange={handleRoleChange}
            >
              <MenuItem value="admin">admin</MenuItem>
              <MenuItem value="super_admin">super_admin</MenuItem>
            </Select>
          </FormControl>
          {formData.role === "admin" && (
            <FormControl fullWidth required sx={{ mb: 2 }} disabled={isSubmitting}>
              <InputLabel id="admin-user-company-label">会社</InputLabel>
              <Select
                labelId="admin-user-company-label"
                label="会社"
                value={formData.companyId}
                onChange={handleChange("companyId")}
              >
                {companies.map((company) => (
                  <MenuItem key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange("isActive")}
                disabled={isSubmitting}
              />
            }
            label="有効"
            sx={{ mb: 2 }}
          />
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              更新
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default AdminUserDetail;
